import { useEffect, useRef } from 'react';
import { chain, gear_left, gear_right } from '../../../images';

export default function GearChains({ move = false }) {
  const chainLeftTrackRef = useRef(null);
  const chainRightTrackRef = useRef(null);

  const gearLeftWrapRef = useRef(null);
  const gearRightWrapRef = useRef(null);
  const gearLeftImgRef = useRef(null);
  const gearRightImgRef = useRef(null);

  const timersRef = useRef({ startSeq: null });

  const animsRef = useRef({
    gearLeft: null,
    gearRight: null,
    chainLeft: null,
    chainRight: null,
    wasMoving: false,
    bumpRunning: false,
    startSeqRunning: false,
  });

  // ---------------------------------------
  // helpers
  // ---------------------------------------
  const readCssMs = (varName, fallbackMs) => {
    try {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim();
      if (!raw) return fallbackMs;

      if (raw.endsWith('ms'))
        return Number(raw.replace('ms', '').trim()) || fallbackMs;
      if (raw.endsWith('s'))
        return (Number(raw.replace('s', '').trim()) || 0) * 1000 || fallbackMs;

      const n = Number(raw);
      return Number.isFinite(n) ? n : fallbackMs;
    } catch {
      return fallbackMs;
    }
  };

  const clearStartSeqTimer = () => {
    if (timersRef.current.startSeq) {
      window.clearTimeout(timersRef.current.startSeq);
      timersRef.current.startSeq = null;
    }
  };

  const getTiming = () => {
    const slackMs = readCssMs('--chains-slack-ms', 520);
    const gearSpinMs = readCssMs('--gear-spin-ms', 3400);
    const chainScrollMs = readCssMs('--chain-scroll-ms', 2400);
    const CHAIN_PX = 1800;
    return { slackMs, gearSpinMs, chainScrollMs, CHAIN_PX };
  };

  const ensureAnimations = () => {
    const gearLeftEl = gearLeftImgRef.current;
    const gearRightEl = gearRightImgRef.current;
    const chainLeftTrackEl = chainLeftTrackRef.current;
    const chainRightTrackEl = chainRightTrackRef.current;

    if (!gearLeftEl || !gearRightEl || !chainLeftTrackEl || !chainRightTrackEl)
      return;

    const a = animsRef.current;
    const { gearSpinMs, chainScrollMs, CHAIN_PX } = getTiming();

    if (!a.gearLeft) {
      a.gearLeft = gearLeftEl.animate(
        [{ transform: 'rotate(0deg)' }, { transform: 'rotate(-360deg)' }],
        { duration: gearSpinMs, iterations: Infinity, easing: 'linear' }
      );
      a.gearLeft.pause();
    }

    if (!a.gearRight) {
      a.gearRight = gearRightEl.animate(
        [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
        { duration: gearSpinMs, iterations: Infinity, easing: 'linear' }
      );
      a.gearRight.pause();
    }

    if (!a.chainLeft) {
      a.chainLeft = chainLeftTrackEl.animate(
        [
          { transform: 'translateY(0px)' },
          { transform: `translateY(-${CHAIN_PX}px)` },
        ],
        { duration: chainScrollMs, iterations: Infinity, easing: 'linear' }
      );
      a.chainLeft.pause();
    }

    if (!a.chainRight) {
      a.chainRight = chainRightTrackEl.animate(
        [
          { transform: 'translateY(0px)' },
          { transform: `translateY(-${CHAIN_PX}px)` },
        ],
        { duration: chainScrollMs, iterations: Infinity, easing: 'linear' }
      );
      a.chainRight.pause();
    }
  };

  const playAllForward = () => {
    const a = animsRef.current;
    if (!a.gearLeft || !a.gearRight || !a.chainLeft || !a.chainRight) return;

    a.gearLeft.play();
    a.gearRight.play();
    a.chainLeft.play();
    a.chainRight.play();

    a.wasMoving = true;
  };

  const pauseAll = () => {
    const a = animsRef.current;
    a.gearLeft?.pause();
    a.gearRight?.pause();
    a.chainLeft?.pause();
    a.chainRight?.pause();
  };

  // ---------------------------------------
  // STOP: hard reverse → clack → settle
  // ---------------------------------------
  const STOP_BUMP_MS = 800;
  const GEAR_KICK = 26;
  const GEAR_CATCH = 8;
  const CHAIN_KICK = 64;
  const CHAIN_CATCH = 10;

  const bumpAndStop = () => {
    const a = animsRef.current;
    if (!a.wasMoving || a.bumpRunning) return;

    pauseAll();
    a.bumpRunning = true;

    const gearLeftWrap = gearLeftWrapRef.current;
    const gearRightWrap = gearRightWrapRef.current;
    const chainLeftEl = chainLeftTrackRef.current;
    const chainRightEl = chainRightTrackRef.current;

    if (!gearLeftWrap || !gearRightWrap || !chainLeftEl || !chainRightEl)
      return;

    const bumpOpts = {
      duration: STOP_BUMP_MS,
      easing: 'linear',
      fill: 'forwards',
    };

    const bumpGL = gearLeftWrap.animate(
      [
        { transform: 'rotate(0deg)', offset: 0 },
        { transform: `rotate(${GEAR_KICK}deg)`, offset: 0.28 },
        { transform: `rotate(${-GEAR_CATCH}deg)`, offset: 0.4 },
        { transform: `rotate(${-GEAR_CATCH}deg)`, offset: 0.62 }, // HOLD
        { transform: `rotate(${-GEAR_CATCH * 0.35}deg)`, offset: 0.78 },
        { transform: 'rotate(0deg)', offset: 1 },
      ],
      bumpOpts
    );

    const bumpGR = gearRightWrap.animate(
      [
        { transform: 'rotate(0deg)', offset: 0 },
        { transform: `rotate(${-GEAR_KICK}deg)`, offset: 0.28 },
        { transform: `rotate(${GEAR_CATCH}deg)`, offset: 0.4 },
        { transform: `rotate(${GEAR_CATCH}deg)`, offset: 0.62 }, // HOLD
        { transform: `rotate(${GEAR_CATCH * 0.35}deg)`, offset: 0.78 },
        { transform: 'rotate(0deg)', offset: 1 },
      ],
      bumpOpts
    );

    const bumpCL = chainLeftEl.animate(
      [
        { transform: 'translateY(0px)', offset: 0 },
        { transform: `translateY(${CHAIN_KICK}px)`, offset: 0.22 },
        { transform: `translateY(${-CHAIN_CATCH}px)`, offset: 0.4 },
        { transform: `translateY(${-CHAIN_CATCH}px)`, offset: 0.55 },
        { transform: 'translateY(0px)', offset: 1 },
      ],
      bumpOpts
    );

    const bumpCR = chainRightEl.animate(
      [
        { transform: 'translateY(0px)', offset: 0 },
        { transform: `translateY(${CHAIN_KICK}px)`, offset: 0.22 },
        { transform: `translateY(${-CHAIN_CATCH}px)`, offset: 0.4 },
        { transform: `translateY(${-CHAIN_CATCH}px)`, offset: 0.55 },
        { transform: 'translateY(0px)', offset: 1 },
      ],
      bumpOpts
    );

    Promise.allSettled([
      bumpGL.finished,
      bumpGR.finished,
      bumpCL.finished,
      bumpCR.finished,
    ]).finally(() => {
      a.bumpRunning = false;
      a.wasMoving = false;
    });
  };

  // ---------------------------------------
  // lifecycle
  // ---------------------------------------
  useEffect(() => {
    ensureAnimations();

    if (move) playAllForward();
    else bumpAndStop();

    return () => {
      clearStartSeqTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [move]);

  return (
    <div className="gearChains">
      <div className="gearChains__group gearChains__group--left">
        <div
          ref={gearLeftWrapRef}
          className="gearChains__gearWrap gearChains__gearWrap--left"
        >
          <img
            ref={gearLeftImgRef}
            src={gear_left}
            alt="gear"
            className="gearChains__gearImg"
            draggable={false}
          />
        </div>

        <div className="gearChains__chainCol gearChains__chainCol--left">
          <div ref={chainLeftTrackRef} className="gearChains__chainTrack">
            <img src={chain} alt="" className="gearChains__chainImg" />
            <img src={chain} alt="" className="gearChains__chainImg" />
            <img src={chain} alt="" className="gearChains__chainImg" />
          </div>
        </div>
      </div>

      <div className="gearChains__group gearChains__group--right">
        <div
          ref={gearRightWrapRef}
          className="gearChains__gearWrap gearChains__gearWrap--right"
        >
          <img
            ref={gearRightImgRef}
            src={gear_right}
            alt="gear"
            className="gearChains__gearImg"
            draggable={false}
          />
        </div>

        <div className="gearChains__chainCol gearChains__chainCol--right">
          <div ref={chainRightTrackRef} className="gearChains__chainTrack">
            <img src={chain} alt="" className="gearChains__chainImg" />
            <img src={chain} alt="" className="gearChains__chainImg" />
            <img src={chain} alt="" className="gearChains__chainImg" />
          </div>
        </div>
      </div>
    </div>
  );
}
